'use client';
import { handleLikeCommentUtil, updateCommentsWithLike } from "@/components/confessions/confession-like";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSidebar } from "@/components/ui/sidebar";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Comment, ParentType } from "@/types/confession";
import { MapsPlace } from "@/types/maps-place";
import { useUser } from "@clerk/nextjs";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Loader2, MessageSquare, Star, StarHalf } from "lucide-react";
import { nanoid } from "nanoid";
import { useMemo, useState } from "react";
import { z } from "zod";
import CommentItem from "../confessions/Comment-Item";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useRestaurantComments } from "./restaurant-comments";
import RestaurantContact from "./restaurant-contact";
import { RestaurantFeatures } from "./restaurant-features";
import { RestaurantImageCarousel } from "./restaurant-image-carousel";
import { getOpeningStatus } from "@/utils/restaurant/restaurant-card-util";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { usePWA } from "@/hooks/use-pwa";
interface RestaurantDetailsProps {
  restaurant: MapsPlace;
}

// Define the validation schema
const reviewSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  content: z.string()
    .min(10, "Review must be at least 10 characters")
    .max(500, "Review cannot exceed 500 characters")
});

export function RestaurantDetails({ restaurant }: RestaurantDetailsProps) {
  const { status, message, hours } = restaurant.regularOpeningHours?.weekdayDescriptions
    ? getOpeningStatus(restaurant.id, restaurant.regularOpeningHours.weekdayDescriptions)
    : { status: '', message: '', hours: '' };
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [commentText, setCommentText] = useState("");
  const [sortOption, setSortOption] = useState<string>("newest");
  const { comments, setComments, isLoading, refetchComments } = useRestaurantComments(restaurant);
  const { user } = useUser();
  const userId = user?.id || "";
  const maxDepth = 5
  const { state } = useSidebar();
  const isExpanded = state === "expanded";

  const handleReplyClick = (comment: Comment, depth: number) => {
    if (!user?.id) {
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    if (depth >= maxDepth) {
      toast({
        title: "Maximum nesting depth reached",
        description: "Please continue this conversation in a new review.",
      });
    } else {
      setReplyingTo(comment);
      setCommentText("");
    }
  };

  const handleLikeComment = async (commentId: string) => {
    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;
    
    const isLiked = !comment.likedBy?.includes(userId);
    
    try {
      // Optimistic update
      setComments(prevComments => updateCommentsWithLike(prevComments, commentId, userId, isLiked));
      
      // API call
      await handleLikeCommentUtil(
        commentId, 
        userId, 
        restaurant.id, 
        setComments,
        ParentType.RESTAURANT,
        !isLiked,
        user?.username || ""
      );
    } catch (error) {
      // Rollback optimistic update on error
      setComments(prevComments => updateCommentsWithLike(prevComments, commentId, userId, !isLiked));
      
      toast({
        title: "Error",
        description: "Failed to update like. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setCommentText("");
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePostComment = async () => {
    if (!user?.id) {
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (replyingTo) {
        await handleReplySubmit();
      } else {
        await handleReviewSubmit();
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!user?.id) {
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    // Validate review
    const result = reviewSchema.safeParse({
      rating,
      content: commentText,
    });

    if (!result.success) {
      toast({
        title: "Review Required",
        description: result.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    // Create optimistic comment with proper author structure
    const optimisticComment = {
      id: nanoid(),
      content: commentText.trim(),
      user: {
        id: userId,
        username: user?.username || "",
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      parentId: restaurant.id,
      parentType: ParentType.RESTAURANT,
      rating,
      likeCount: 0,
    };

    // Optimistically add the comment
    setComments(prev => [optimisticComment as Comment, ...prev]);
    setCommentText("");
    setRating(0);

    try {
      const response = await fetch("/api/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: commentText.trim(),
          userId: userId,
          userName: user?.username || "",
          parentId: restaurant.id,
          parentType: ParentType.RESTAURANT,
          rating,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save review");
      }

      const savedComment = await response.json();
      
      // Transform the server response to match our Comment type
      const transformedComment = {
        ...savedComment,
        user: {
          id: userId,
          username: savedComment.userName,
        },
        createdAt: new Date(savedComment.createdAt),
        updatedAt: savedComment.updatedAt ? new Date(savedComment.updatedAt) : undefined,
      };

      // Update with the transformed comment
      setComments(prev => 
        prev.map(comment => 
          comment.id === optimisticComment.id ? transformedComment : comment
        )
      );

      toast({
        title: "Success",
        description: "Your review has been posted!",
      });
    } catch (error) {
      // Revert optimistic update on error
      setComments(prev => prev.filter(comment => comment.id !== optimisticComment.id));
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to post your review. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReplySubmit = async () => {
    if (!replyingTo) return;

    const optimisticReply = {
      id: nanoid(),
      content: commentText.trim(),
      user: {
        id: userId,
        username: user?.username || "",
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      parentId: restaurant.id,
      parentType: ParentType.RESTAURANT,
      parentCommentId: replyingTo.id,
      topLevelCommentId: replyingTo.topLevelCommentId || replyingTo.id,
      likeCount: 0,
    };

    // Optimistically add the reply
    setComments(prev => [optimisticReply as Comment, ...prev]);
    setCommentText("");
    setReplyingTo(null);

    try {
      const response = await fetch("/api/comments/reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: commentText.trim(),
          userId: userId,
          userName: user?.username || "",
          parentId: restaurant.id,
          parentType: ParentType.RESTAURANT,
          parentCommentId: replyingTo.id,
          topLevelCommentId: replyingTo.topLevelCommentId || replyingTo.id,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save reply");
      }

      const savedReply = await response.json();
      
      // Transform the server response
      const transformedReply = {
        ...savedReply,
        user: {
          id: userId,
          username: savedReply.userName,
        },
        createdAt: new Date(savedReply.createdAt),
        updatedAt: savedReply.updatedAt ? new Date(savedReply.updatedAt) : undefined,
      };

      // Update with the transformed reply
      setComments(prev => 
        prev.map(comment => 
          comment.id === optimisticReply.id ? transformedReply : comment
        )
      );

      toast({
        title: "Success",
        description: "Your reply has been posted!",
      });
    } catch (error) {
      // Revert optimistic update on error
      setComments(prev => prev.filter(comment => comment.id !== optimisticReply.id));
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to post your reply. Please try again.",
        variant: "destructive",
      });
    }
  };

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      if (sortOption === "oldest") {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortOption === "mostLiked") {
        return (b.likeCount ?? 0) - (a.likeCount ?? 0);
      }
      // Default to "newest"
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [comments, sortOption]);

  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [showReviewInput, setShowReviewInput] = useState(false);
  const { scrollY } = useScroll();
  
  // Track scroll position to show/hide review input
  useMotionValueEvent(scrollY, "change", (latest) => {
    // Show after scrolling past first review (about 200px)
    // If no reviews, show after scrolling 100px
    const threshold = comments.length > 2 ? 600 : 300;
    setShowReviewInput(latest > threshold);
  });

  // Only render top-level comments (ones without parentCommentId)
  const topLevelComments = comments.filter(comment => !comment.parentCommentId);
  const isPWA = usePWA();

  return (
    <div className="pb-56">
            <div>
        <header className={`fixed ${isPWA ? 'top-0' : 'top-12'} z-10 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b`}>
          <div className="flex h-16 items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="/restaurants">
                    Restaurants
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{restaurant.displayName?.text}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
      </div>
      <Card id="overview" className="w-full">
        <CardHeader id="basic-info" className="space-y-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <h1 className="text-2xl font-bold pt-2">{restaurant.displayName?.text}</h1>
            <div className="flex items-center gap-2">
              <span className="text-xl">{restaurant.rating}</span>
              <div className="flex" id="rating">
                {Array.from({ length: 5 }).map((_, index) => {
                  const rating = restaurant.rating || 0;
                  const filled = rating >= index + 1;
                  const halfFilled = rating >= index + 0.5;
                  
                  return (
                    <motion.span 
                      key={index}
                      className="text-amber-500"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {filled ? (
                        <Star className="w-5 h-5 fill-current" />
                      ) : halfFilled ? (
                        <StarHalf className="w-5 h-5 fill-current" />
                      ) : (
                        <Star className="w-5 h-5" />
                      )}
                    </motion.span>
                  );
                })}
              </div>
              <span className="text-muted-foreground">
                ({restaurant.userRatingCount} reviews)
              </span>
            </div>
          </div>
          
          <div id="hours" className={`text-sm font-medium ${
            status === 'open' ? 'text-green-600' : 
            status === 'closing-soon' ? 'text-yellow-600' : 
            'text-red-600'
          }`}>
            {message} • {hours}
          </div>
        </CardHeader>

        <CardContent id="photos" className="space-y-6">
          <div id="gallery" className="items-center justify-center rounded-lg relative overflow-hidden">
            <RestaurantImageCarousel placeId={restaurant.id} />
          </div>
          
          <div id="contact">
            <RestaurantContact 
              googleMapsUri={restaurant.googleMapsUri ?? ''}
              internationalPhoneNumber={restaurant.internationalPhoneNumber ?? ''}
              shortFormattedAddress={restaurant.shortFormattedAddress ?? ''}
              websiteUri={restaurant.websiteUri ?? ''}
              id={restaurant.id}
            />
          </div>

          <div id="features">
            <RestaurantFeatures features={restaurant} />
          </div>
        </CardContent>
      </Card>

      {/* Reviews Section */}
      <div id="reviews" className="mt-6">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex-1" /> {/* Spacer */}
              <div className="flex items-center gap-4">
                <CardTitle>Reviews ({comments.length})</CardTitle>
                <Select value={sortOption} onValueChange={setSortOption}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oldest">Oldest</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="mostLiked">Most Liked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent className="w-full sm:min-w-[690px] lg:min-w-[1175px]">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : comments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  No reviews yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  Be the first to review this restaurant!
                </p>
                <Button 
                  className="mt-4"
                  onClick={() => setShowReviewInput(true)} // Allow showing input from empty state
                >
                  Write a Review
                </Button>
              </div>
            ) : (
              sortedComments.filter(comment => !comment.parentCommentId).map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  handleReplyClick={handleReplyClick}
                  handleLikeComment={handleLikeComment}
                  replyingTo={replyingTo}
                  setCommentText={setCommentText}
                  handleCancelReply={handleCancelReply}
                  handlePostComment={handlePostComment}
                  commentText={commentText}
                  depth={0}
                  maxDepth={5}
                  userId={userId}
                  allComments={sortedComments}
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Input */}
      {!replyingTo && (
        <div id="write-review">
          <motion.div 
            className="fixed bottom-0 left-0 right-0"
            initial={{ y: 100, opacity: 0 }}
            animate={{ 
              y: showReviewInput ? 0 : 100, 
              opacity: showReviewInput ? 1 : 0 
            }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20
            }}
          >
            <div className="px-2 sm:px-4 md:px-8">
              <Card 
                className={cn(
                  "w-full transition-all duration-300",
                  state === "expanded" 
                    ? "md:max-w-[calc(100%-16rem)] md:ml-64" 
                    : "max-w-full md:max-w-[calc(100%-2rem)] md:ml-10"
                )}
              >
                <CardContent className={cn("p-2 sm:p-4", isPWA && "mb-20")}>
                  <div className="flex flex-col space-y-2 sm:space-y-4">
                    {/* Star Rating */}
                    <motion.div 
                      className="flex items-center space-x-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className="focus:outline-none transition-colors"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                        >
                          <Star
                            className={`w-6 h-6 ${
                              star <= (hoverRating || rating)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300 dark:text-gray-600'
                            } transition-colors`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        {rating ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select rating'}
                      </span>
                    </motion.div>

                    {/* Textarea */}
                    <motion.textarea
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="w-full p-2 sm:p-3 border rounded-lg resize-none 
                                dark:bg-zinc-700 dark:border-gray-600 dark:text-gray-300
                                focus:outline-none focus:ring-1 focus:ring-primary"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Write a review..."
                    />
                    
                    {/* Buttons */}
                    <motion.div 
                      className="flex flex-row sm:flex-row justify-end gap-1 sm:gap-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          handleCancelReply();
                          setRating(0);
                        }}
                        className="w-full sm:w-auto text-sm"
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handlePostComment} 
                        disabled={isSubmitting}
                        className="w-full sm:w-auto text-sm"
                      >
                        {isSubmitting ? "Posting..." : "Post Review"}
                      </Button>
                    </motion.div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
} 